import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Message as MessageType } from '../types';

const CommunicationPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MessageType | null>(null);
  const [conversationMessages, setConversationMessages] = useState<MessageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composing, setComposing] = useState(false);
  const [newMessage, setNewMessage] = useState({
    recipientId: '',
    subject: '',
    content: ''
  });
  const [dhUsers, setDhUsers] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch DH users (for Governor to communicate with)
  const fetchDhUsers = async () => {
    try {
      const response = await api.getUsers('DH');
      if (response.success) {
        setDhUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch DH users:', err);
    }
  };

  // Fetch received messages
  const fetchReceivedMessages = async () => {
    setLoading(true);
    try {
      const response = await api.get('/messages/inbox');
      if (response.success) {
        setMessages(response.data);
        setError('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch sent messages
  const fetchSentMessages = async () => {
    setLoading(true);
    try {
      const response = await api.get('/messages/sent');
      if (response.success) {
        setMessages(response.data);
        setError('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch conversation with selected user
  const fetchConversation = async (messageId: string) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg) return;

    setSelectedMessage(msg);
    setLoading(true);

    try {
      const otherUserId = msg.senderId === user?.id ? msg.recipientId : msg.senderId;
      const response = await api.get(`/messages/conversation/${otherUserId}`);
      if (response.success) {
        setConversationMessages(response.data);
        // Mark as read if it's an incoming message
        if (msg.recipientId === user?.id) {
          await api.put(`/messages/${messageId}/read`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  // Send a new message
  const handleSendMessage = async () => {
    if (!newMessage.recipientId || !newMessage.subject || !newMessage.content) {
      setError('Please fill in all fields');
      return;
    }

    setComposing(true);
    try {
      const response = await api.post('/messages', newMessage);
      if (response.success) {
        setSuccess('Message sent successfully');
        setNewMessage({ recipientId: '', subject: '', content: '' });
        setComposeOpen(false);
        await fetchSentMessages();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setComposing(false);
    }
  };

  // Delete a message
  const handleDeleteMessage = async (messageId: string) => {
    try {
      const response = await api.delete(`/messages/${messageId}`);
      if (response.success) {
        setMessages(messages.filter(m => m.id !== messageId));
        setSuccess('Message deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete message');
    }
  };

  useEffect(() => {
    fetchDhUsers();
    if (user?.role === 'Governor') {
      fetchReceivedMessages();
    }
  }, [user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0) {
      fetchReceivedMessages();
    } else {
      fetchSentMessages();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        {user?.role === 'Governor' ? 'Communicate with DH' : 'Messages'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Messages List */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Messages</Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setComposeOpen(true);
                    setNewMessage({ recipientId: '', subject: '', content: '' });
                  }}
                >
                  New Message
                </Button>
              </Box>

              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ mb: 2 }}
              >
                <Tab label="Inbox" />
                <Tab label="Sent" />
              </Tabs>

              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <List sx={{ maxHeight: '600px', overflow: 'auto' }}>
                  {messages.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                      No messages yet
                    </Typography>
                  ) : (
                    messages.map(message => (
                      <ListItem
                        key={message.id}
                        disablePadding
                        sx={{
                          backgroundColor: tabValue === 0 && !message.readAt ? '#f0f0f0' : 'transparent'
                        }}
                      >
                        <ListItemButton
                          onClick={() => fetchConversation(message.id)}
                          selected={selectedMessage?.id === message.id}
                        >
                          <Avatar sx={{ mr: 2 }}>
                            {tabValue === 0
                              ? message.sender?.firstName?.[0].toUpperCase()
                              : message.recipient?.firstName?.[0].toUpperCase()}
                          </Avatar>
                          <ListItemText
                            primary={message.subject}
                            secondary={
                              tabValue === 0
                                ? `From: ${message.sender?.firstName} ${message.sender?.lastName}`
                                : `To: ${message.recipient?.firstName} ${message.recipient?.lastName}`
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Message Detail */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {selectedMessage ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {selectedMessage.subject}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {tabValue === 0
                          ? `From: ${selectedMessage.sender?.firstName} ${selectedMessage.sender?.lastName} (${selectedMessage.sender?.email})`
                          : `To: ${selectedMessage.recipient?.firstName} ${selectedMessage.recipient?.lastName} (${selectedMessage.recipient?.email})`}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(selectedMessage.createdAt).toLocaleString()}
                      </Typography>
                      {tabValue === 0 && selectedMessage.readAt && (
                        <Chip label="Read" size="small" sx={{ ml: 1 }} />
                      )}
                    </Box>
                    <Button
                      startIcon={<DeleteIcon />}
                      color="error"
                      size="small"
                      onClick={() => handleDeleteMessage(selectedMessage.id)}
                    >
                      Delete
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ minHeight: '300px', p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                    <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedMessage.content}
                    </Typography>
                  </Box>

                  {/* Conversation History */}
                  {conversationMessages.length > 1 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        Conversation History
                      </Typography>
                      <Paper sx={{ maxHeight: '400px', overflow: 'auto' }}>
                        {conversationMessages.map(msg => (
                          <Box key={msg.id} sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <Typography variant="caption" color="textSecondary">
                              {msg.senderId === user?.id ? 'You' : `${msg.sender?.firstName} ${msg.sender?.lastName}`} -{' '}
                              {new Date(msg.createdAt).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {msg.content}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
                  <Typography variant="body1" color="textSecondary">
                    Select a message to view details
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compose Message Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Message</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {user?.role === 'Governor' ? (
            <TextField
              select
              fullWidth
              label="Send to DH"
              value={newMessage.recipientId}
              onChange={(e) => setNewMessage({ ...newMessage, recipientId: e.target.value })}
              SelectProps={{
                native: true,
              }}
              sx={{ mb: 2 }}
            >
              <option value="">Select a DH</option>
              {dhUsers.map(dh => (
                <option key={dh.id} value={dh.id}>
                  {dh.firstName} {dh.lastName}
                </option>
              ))}
            </TextField>
          ) : (
            <TextField
              fullWidth
              label="Recipient ID"
              value={newMessage.recipientId}
              onChange={(e) => setNewMessage({ ...newMessage, recipientId: e.target.value })}
              placeholder="Enter recipient user ID"
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth
            label="Subject"
            value={newMessage.subject}
            onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
            placeholder="Message subject"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Message"
            value={newMessage.content}
            onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
            placeholder="Type your message here..."
            multiline
            rows={5}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={composing}
            startIcon={composing ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CommunicationPage;
